import { parseSession, type AuthenticatedSession } from "./domain";

export interface MfaChallenge {
  readonly challengeToken: string;
  readonly expiresAt: Date;
  readonly setup: { readonly secret: string; readonly qrCode: string } | null;
}

export function parseMfaChallenge(value: unknown): MfaChallenge {
  if (typeof value !== "object" || value === null) throw new TypeError("Invalid MFA challenge");
  const data = value as Record<string, unknown>;
  if (
    typeof data.challenge_token !== "string" ||
    data.challenge_token.length !== 43 ||
    typeof data.expires_at !== "number" ||
    !Number.isSafeInteger(data.expires_at) ||
    data.expires_at <= 0
  )
    throw new TypeError("Invalid MFA challenge");
  let setup: MfaChallenge["setup"] = null;
  if (data.setup !== undefined) {
    if (typeof data.setup !== "object" || data.setup === null)
      throw new TypeError("Invalid MFA setup");
    const fields = data.setup as Record<string, unknown>;
    if (
      typeof fields.secret !== "string" ||
      !/^[A-Z2-7]{32}$/.test(fields.secret) ||
      typeof fields.qr_code !== "string" ||
      !/^[A-Za-z0-9+/]+=*$/.test(fields.qr_code)
    )
      throw new TypeError("Invalid MFA setup");
    setup = { secret: fields.secret, qrCode: fields.qr_code };
  }
  return {
    challengeToken: data.challenge_token,
    expiresAt: new Date(data.expires_at * 1000),
    setup,
  };
}

export function parseMfaSession(value: unknown): {
  session: AuthenticatedSession;
  recoveryCodes: readonly string[];
} {
  const session = parseSession(value);
  const codes = (value as Record<string, unknown>).recovery_codes;
  if (
    codes !== undefined &&
    (!Array.isArray(codes) ||
      codes.length !== 10 ||
      !codes.every(
        (code: unknown) =>
          typeof code === "string" && /^(?:[a-f0-9]{4}-){7}[a-f0-9]{4}$/.test(code),
      ))
  )
    throw new TypeError("Invalid recovery codes");
  return { session, recoveryCodes: codes ?? [] };
}
